"use client";

import { ReactNode, useEffect, useRef } from "react";

// Minimal drop-in replacement for react-ui-scrollspy used by the terms pages.
// Watches every direct-child <section id="..."> inside its container, and toggles
// `activeClass` on the sidebar anchor whose href ends with `#<id>` of the most
// visible section. Uses IntersectionObserver — no scroll listeners.
type Props = {
  activeClass: string;
  children: ReactNode;
  // Kept for prop-compat with react-ui-scrollspy; ignored.
  useBoxMethod?: boolean;
};

export default function ScrollSpy({ activeClass, children }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const sections = Array.from(
      container.querySelectorAll<HTMLElement>(":scope > section[id]"),
    );
    if (sections.length === 0) return;

    const visibility = new Map<string, number>();

    const setActive = (id: string) => {
      const anchors =
        document.querySelectorAll<HTMLAnchorElement>(`a[href$="#${id}"]`);
      // Clear active class from every anchor that could be a target, then set it.
      document
        .querySelectorAll<HTMLAnchorElement>("a[href*='#']")
        .forEach((a) => {
          activeClass.split(/\s+/).forEach((c) => c && a.classList.remove(c));
        });
      anchors.forEach((a) => {
        activeClass.split(/\s+/).forEach((c) => c && a.classList.add(c));
      });
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          visibility.set(entry.target.id, entry.intersectionRatio);
        });
        let bestId = "";
        let bestRatio = 0;
        visibility.forEach((ratio, id) => {
          if (ratio > bestRatio) {
            bestRatio = ratio;
            bestId = id;
          }
        });
        if (bestId) setActive(bestId);
      },
      { rootMargin: "-20% 0px -60% 0px", threshold: [0, 0.25, 0.5, 0.75, 1] },
    );

    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, [activeClass]);

  return <div ref={containerRef}>{children}</div>;
}
